import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useChargingSessionsByGroup from "../hooks/useChargingSessionsByGroup";
import useFetchAllStations from "../hooks/useFetchAllStations";
import usePaymentTransactions from "../hooks/usePaymentTransactions";
import { formatINR } from "../utils/formatINR";
import {
  addDays,
  dailySeries,
  filterSessions,
  filterTransactions,
  formatDecimal,
  formatDuration,
  formatNumber,
  percentChange,
  previousRange,
  summarizeSessions,
  todayKey,
  daysBetween,
} from "../utils/analyticsMetrics";
import { Section } from "../components/Analytics/AnalyticsUI";
import AnalyticsFilters, { LastUpdated, PRESETS } from "../components/Analytics/AnalyticsFilters";
import KPICard, { DeltaBadge, Sparkline } from "../components/Analytics/KPICard";
import RevenueChart from "../components/Analytics/RevenueChart";
import ChargingAnalytics from "../components/Analytics/ChargingAnalytics";
import TransactionBreakdown from "../components/Analytics/TransactionBreakdown";
import StationPerformance from "../components/Analytics/StationPerformance";
import VehicleAnalytics from "../components/Analytics/VehicleAnalytics";
import SettlementSummary from "../components/Analytics/SettlementSummary";
import { Skeleton } from "../components/Analytics/AnalyticsUI";

const rangeForPreset = (preset) => {
  const days = PRESETS.find((p) => p.value === preset)?.days || 30;
  const to = todayKey();
  return { fromDate: addDays(to, -(days - 1)), toDate: to };
};

// The backend expects plain YYYY-MM-DD dates and makes the end date inclusive itself
// (it appends 00:00:00 / 23:59:59), so dates are sent as-is.
const toApiRange = (r) => ({ fromDate: r.fromDate, toDate: r.toDate });

// remembers when each range was last fetched, across visits in this tab
const lastFetchedAt = new Map();

// Loaded only for administrators: the payment transactions API is not scoped by
// group, so other roles must not request it.
const AdminTransactionsSource = ({ apiRange, onChange }) => {
  const { transactions, isLoading, error, refetch } = usePaymentTransactions(apiRange);
  // refetch is a new function every render; keep it in a ref so it doesn't retrigger the effect
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const stableRefetch = useCallback(() => refetchRef.current(), []);
  useEffect(() => {
    onChange({ transactions, isLoading, error, refetch: stableRefetch });
  }, [transactions, isLoading, error, stableRefetch, onChange]);
  return null;
};

const Analytics = () => {
  const groupId = localStorage.getItem("group_id");
  const isAdmin = groupId === "1";

  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState(() => rangeForPreset("30d"));
  const [stationFilter, setStationFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [revenueGranularity, setRevenueGranularity] = useState("daily");
  const [chargingGranularity, setChargingGranularity] = useState("daily");
  const [tx, setTx] = useState({ transactions: [], isLoading: isAdmin, error: "", refetch: null });

  const prevRange = useMemo(() => previousRange(range), [range]);
  const apiRange = useMemo(() => toApiRange(range), [range]);
  const apiPrevRange = useMemo(() => toApiRange(prevRange), [prevRange]);

  const current = useChargingSessionsByGroup(groupId, apiRange, { withSettlement: true });
  const previous = useChargingSessionsByGroup(groupId, apiPrevRange);
  const { stations, refetch: refetchStations } = useFetchAllStations({ groupId });

  // granularity defaults that suit the period length
  useEffect(() => {
    const days = daysBetween(range.fromDate, range.toDate);
    const g = days > 120 ? "monthly" : days > 45 ? "weekly" : "daily";
    setRevenueGranularity(g);
    setChargingGranularity(g);
  }, [range.fromDate, range.toDate]);

  // last updated: stamped when a fetch for this range completes
  const rangeKey = `${range.fromDate}:${range.toDate}`;
  const [lastUpdated, setLastUpdated] = useState(() => lastFetchedAt.get(rangeKey) || null);
  const wasLoading = useRef(current.isLoading);
  useEffect(() => {
    if (wasLoading.current && !current.isLoading && !current.error) {
      const now = new Date();
      lastFetchedAt.set(rangeKey, now);
      setLastUpdated(now);
    } else if (!current.isLoading) {
      setLastUpdated(lastFetchedAt.get(rangeKey) || null);
    }
    wasLoading.current = current.isLoading;
  }, [current.isLoading, current.error, rangeKey]);

  const handlePreset = (value) => {
    setPreset(value);
    if (value !== "custom") setRange(rangeForPreset(value));
  };

  const handleRefresh = useCallback(() => {
    current.refetch();
    previous.refetch();
    tx.refetch?.();
    refetchStations?.();
  }, [current, previous, tx, refetchStations]);

  const handleTxChange = useCallback((next) => setTx(next), []);

  // ---------- derived data ----------

  const stationNames = useMemo(() => {
    const map = {};
    for (const s of stations || []) {
      if (s.stationId) map[s.stationId] = { name: s.stationName || "", location: s.locationName || "" };
    }
    return map;
  }, [stations]);

  const filters = { stationId: stationFilter, groupId: groupFilter };
  const sessions = useMemo(() => filterSessions(current.sessions, filters), [current.sessions, stationFilter, groupFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevSessions = useMemo(() => filterSessions(previous.sessions, filters), [previous.sessions, stationFilter, groupFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const transactions = useMemo(() => filterTransactions(tx.transactions, { stationId: stationFilter }), [tx.transactions, stationFilter]);

  const summary = useMemo(() => summarizeSessions(sessions), [sessions]);
  const prevSummary = useMemo(() => summarizeSessions(prevSessions), [prevSessions]);
  const daily = useMemo(() => dailySeries(sessions, range.fromDate, range.toDate), [sessions, range.fromDate, range.toDate]);

  const stationOptions = useMemo(() => {
    const ids = new Set([...Object.keys(stationNames), ...(current.sessions || []).map((s) => s.station_id).filter(Boolean)]);
    return [...ids]
      .map((id) => ({ value: id, label: stationNames[id]?.name ? `${stationNames[id].name} (${id})` : id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [stationNames, current.sessions]);

  const groupOptions = useMemo(
    () => (isAdmin ? [...new Set((current.sessions || []).map((s) => s.group_id).filter(Boolean))].sort() : []),
    [isAdmin, current.sessions]
  );

  const loading = current.isLoading;
  const compareLoading = previous.isLoading;
  const change = (key) => (compareLoading || previous.error ? undefined : percentChange(summary[key], prevSummary[key]));
  const days = daysBetween(range.fromDate, range.toDate);
  const showSpark = daily.length > 1;
  const sessionsError = current.error && !current.sessions?.length ? current.error : "";
  const rangeLabel = days === 1 ? "today" : `the last ${days} days`;

  return (
    <div className="mx-auto max-w-[1400px] space-y-8 pb-10">
      {isAdmin && <AdminTransactionsSource apiRange={apiRange} onChange={handleTxChange} />}

      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="page-title">Analytics</h1>
            <p className="page-subtitle">Monitor charging activity, revenue and platform performance.</p>
          </div>
          <LastUpdated lastUpdated={lastUpdated} />
        </div>
        <AnalyticsFilters
          preset={preset}
          onPresetChange={handlePreset}
          range={range}
          onCustomRangeChange={setRange}
          stationId={stationFilter}
          onStationChange={setStationFilter}
          stationOptions={stationOptions}
          groupId={groupFilter}
          onGroupChange={setGroupFilter}
          groupOptions={groupOptions}
          onRefresh={handleRefresh}
          isRefreshing={loading}
        />
        {current.error && current.sessions?.length > 0 && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-13 text-amber-800">
            Showing the last loaded data; refreshing failed: {current.error}
          </p>
        )}
      </div>

      {/* KPIs */}
      <section aria-label="Key metrics" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card flex flex-col p-5 sm:col-span-2 lg:row-span-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-13 font-medium text-neutral-600">Total revenue</p>
              <p className="text-xs text-muted">Charging revenue billed for {rangeLabel}, incl. GST</p>
            </div>
            {!loading && <DeltaBadge change={change("revenue")} />}
          </div>
          {loading ? (
            <Skeleton className="mt-4 h-10 w-48" />
          ) : sessionsError ? (
            <p className="mt-4 text-13 text-red-700">Couldn't load revenue. {sessionsError}</p>
          ) : (
            <p className="mt-3 text-[34px] font-semibold leading-none tracking-tight tabular-nums text-fg">{formatINR(summary.revenue)}</p>
          )}
          {!loading && !sessionsError && (
            <p className="mt-2 text-13 text-muted">
              vs <span className="tabular-nums">{compareLoading ? "…" : formatINR(prevSummary.revenue)}</span> in the previous {days === 1 ? "day" : `${days} days`}
            </p>
          )}
          <div className="mt-4 min-h-[96px] flex-1">
            {loading ? <Skeleton className="h-full min-h-[96px] w-full" /> : showSpark && <Sparkline data={daily} dataKey="revenue" height={110} formatter={formatINR} />}
          </div>
          {!loading && !sessionsError && (
            <div className="mt-4 grid grid-cols-3 gap-4 border-t border-line pt-4">
              <div>
                <p className="text-xs text-muted">GST included</p>
                <p className="text-13 font-semibold tabular-nums text-fg">{formatINR(summary.gst)}</p>
              </div>
              <div>
                <p className="text-xs text-muted">Daily average</p>
                <p className="text-13 font-semibold tabular-nums text-fg">{formatINR(summary.revenue / days)}</p>
              </div>
              <div>
                <p className="text-xs text-muted">Best day</p>
                <p className="text-13 font-semibold tabular-nums text-fg">{formatINR(Math.max(0, ...daily.map((d) => d.revenue)))}</p>
              </div>
            </div>
          )}
        </div>

        <KPICard
          label="Energy delivered"
          value={formatDecimal(summary.energy)}
          unit="kWh"
          change={change("energy")}
          isLoading={loading}
          spark={showSpark && <Sparkline data={daily} dataKey="energy" formatter={(v) => `${formatDecimal(v)} kWh`} />}
        />
        <KPICard
          label="Charging sessions"
          value={formatNumber(summary.sessions)}
          hint={`${formatNumber(summary.sessionsWithEnergy)} delivered energy`}
          change={change("sessions")}
          isLoading={loading}
          spark={showSpark && <Sparkline data={daily} dataKey="sessions" formatter={formatNumber} />}
        />
        <KPICard
          label="Active vehicles"
          value={formatNumber(summary.vehicles)}
          hint="Distinct vehicles that charged"
          change={change("vehicles")}
          isLoading={loading}
          spark={showSpark && <Sparkline data={daily} dataKey="vehicles" formatter={formatNumber} />}
        />
        <div className="card flex flex-col p-4">
          <p className="text-13 font-medium text-neutral-600">Per session</p>
          <div className="mt-3 space-y-3">
            <div className="flex items-end justify-between gap-2">
              <div>
                <p className="text-xs text-muted">Avg revenue</p>
                {loading ? <Skeleton className="mt-1 h-6 w-24" /> : <p className="text-xl font-semibold tabular-nums text-fg">{formatINR(summary.avgRevenuePerSession)}</p>}
              </div>
              {!loading && <DeltaBadge change={change("avgRevenuePerSession")} />}
            </div>
            <div className="flex items-end justify-between gap-2 border-t border-line pt-3">
              <div>
                <p className="text-xs text-muted">Avg duration</p>
                {loading ? <Skeleton className="mt-1 h-6 w-20" /> : <p className="text-xl font-semibold tabular-nums text-fg">{formatDuration(summary.avgDurationSeconds)}</p>}
              </div>
              {!loading && <DeltaBadge change={change("avgDurationSeconds")} />}
            </div>
          </div>
        </div>
      </section>

      <Section title="Revenue" description="How much charging revenue was generated, and whether it's growing.">
        <RevenueChart
          sessions={sessions}
          transactions={transactions}
          showRefunds={isAdmin}
          range={range}
          granularity={revenueGranularity}
          onGranularityChange={setRevenueGranularity}
          isLoading={loading}
          error={sessionsError}
          refundsLoading={tx.isLoading}
          refundsError={tx.error}
        />
      </Section>

      <Section title="Settlements" description="How much of the period's charging revenue has been settled to station owners.">
        <SettlementSummary sessions={sessions} isLoading={loading} available={current.settlementAvailable} />
      </Section>

      <Section title="Charging activity" description="Session volume, energy delivered and when drivers charge.">
        <ChargingAnalytics
          sessions={sessions}
          summary={summary}
          range={range}
          granularity={chargingGranularity}
          onGranularityChange={setChargingGranularity}
          isLoading={loading}
          error={sessionsError}
        />
      </Section>

      <Section title="Transactions" description="Wallet and payment transactions by type and outcome. These are financial movements, not charging sessions.">
        <TransactionBreakdown
          isAdmin={isAdmin}
          transactions={transactions}
          isLoading={tx.isLoading && !tx.transactions?.length}
          error={tx.error && !tx.transactions?.length ? tx.error : ""}
          stationFiltered={!!stationFilter}
        />
      </Section>

      <Section title="Station performance" description="Which stations drive revenue and energy, and which need attention.">
        <StationPerformance sessions={sessions} stationNames={stationNames} isLoading={loading} error={sessionsError} />
      </Section>

      <Section title="Vehicles" description="How many vehicles use the network and how often they come back.">
        <VehicleAnalytics sessions={sessions} isLoading={loading} error={sessionsError} />
      </Section>
    </div>
  );
};

export default Analytics;
